import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Button, Container, CircularProgress, Alert, useTheme, useMediaQuery, Paper, Divider, Tooltip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Work as WorkIcon,
    PersonAdd as CreatorIcon,
    Edit as EditIcon,
    Assignment,
    AssignmentAdd,
    AssignmentTurnedIn
} from '@mui/icons-material';
import api from '../../../services/api';

const DetailsEntrepreneur = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [typeUser, setTypeUser] = useState(null);
    const [forms, setForms] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentSection, setCurrentSection] = useState(0);

    const sections = [
        { title: 'Empresa', icon: <BusinessIcon sx={{ mr: 1, color: '#10641c' }} /> },
        { title: 'Responsável', icon: <PersonIcon sx={{ mr: 1, color: '#10641c' }} /> },
        { title: 'Localização', icon: <LocationIcon sx={{ mr: 1, color: '#10641c' }} /> },
        { title: 'Características', icon: <WorkIcon sx={{ mr: 1, color: '#10641c' }} /> },
        { title: 'Criador', icon: <CreatorIcon sx={{ mr: 1, color: '#10641c' }} /> },
    ];

    useEffect(() => {
        setTypeUser(location.pathname.split('/')[1]);
        const fetchData = async () => {
            try {
                setLoading(true);
                const [entrepreneurResponse, formsResponse] = await Promise.all([
                    api.get(`/economic-agents/${id}`),
                    api.get(`/form-templates/economic-agent/${id}`)
                ]);
                setUser(entrepreneurResponse.data);
                setForms(formsResponse.data);
                setError(null);
            } catch (err) {
                setError('Erro ao carregar os dados');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, location.pathname]);

    const handleSectionChange = (index) => {
        setCurrentSection(index);
    };

    const maskPhoneNumber = (phone) => {
        if (!phone) return 'Não informado';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{2})(\d{1})(\d{4})(\d{4})$/);
        return match
            ? `(${match[1]}) ${match[2]}${match[3]}-${match[4]}`
            : phone || 'Formato inválido';
    };

    const formatCpfCnpj = (value) => {
        if (!value) return 'Não informado';
        const cleaned = value.replace(/\D/g, '');

        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (cleaned.length === 14) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return value || 'Formato inválido';
    };

    const renderBusinessTypes = () => {
        const selectedActivities = user?.typeBusiness
            ? user.typeBusiness
                .map((tb) => tb?.name)
                .filter(Boolean)
            : [];

        return selectedActivities.length === 0
            ? 'Nenhum tipo selecionado'
            : selectedActivities.length === 1
                ? selectedActivities[0]
                : selectedActivities.length === 2
                    ? `${selectedActivities[0]} e ${selectedActivities[1]}`
                    : `${selectedActivities.slice(0, -1).join(', ')} e ${selectedActivities[selectedActivities.length - 1]}`;
    };

    const renderSectionContent = () => {
        switch (currentSection) {
            case 0:
                return (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Código:</strong> {user.code}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Nome/Razão Social:</strong> {user.name}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>CPF/CNPJ:</strong> {formatCpfCnpj(user.cpf_cnpj)}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Porte:</strong> {user.businessSize ? user.businessSize.name : 'Não informado'}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Início de Atividades:</strong>{' '}
                            {user.startDate
                                ? new Date(user.startDate).toLocaleDateString('pt-BR')
                                : 'Não informado'}
                        </Typography>
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Nome:</strong> {user.user.name}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Telefone:</strong> {maskPhoneNumber(user.phone)}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Email:</strong> {user.user.email ? user.user.email : 'Não informado'}
                        </Typography>
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Endereço:</strong> {user.address}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Número:</strong> {user.number ? user.number : 'Não informado'}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Zona:</strong> {user.neighborhood.zone === 'urbana' ? 'Urbana' : user.neighborhood.zone === 'rural' ? 'Rural' : user.neighborhood.zone}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Bairro/Distrito:</strong> {user.neighborhood.name}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Ponto de Referência:</strong>{' '}
                            <span>{user.referencePoint ? user.referencePoint : 'Não informado'}</span>
                        </Typography>
                    </Box>
                );
            case 3:
                return (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Tipo de Empreendimento:</strong>{' '}
                            {renderBusinessTypes()}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Alvará:</strong> {user.hasLicense ? 'Possui' : 'Não Possui'}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Cadastro:</strong>{' '}
                            <span style={{ color: user.active === 1 ? '#10641c' : '#d32f2f' }}>
                                {user.active === 1 ? 'Ativo' : 'Inativo'}
                            </span>
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Observações:</strong>{' '}
                            <span>
                                {user.observation ? user.observation : 'Não há observações'}
                            </span>
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Visibilidade no mapa:</strong>{' '}
                            <span>
                                {user.visible ? 'Está no Mapa' : 'Não está no Mapa'}
                            </span>
                        </Typography>
                    </Box>
                );
            case 4:
                return (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Cadastrado Por:</strong> {user.creator?.name || 'Não informado'}
                        </Typography>
                        <Typography sx={{ mb: 1, color: '#424242' }}>
                            <strong>Data do Cadastro:</strong>{' '}
                            {user.creator?.createdAt
                                ? new Date(user.createdAt).toLocaleDateString('pt-BR')
                                : 'Não informado'}
                        </Typography>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ mt: isMobile ? 2 : 0, mb: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    sx={{
                        color: '#10641c',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                    }}
                >
                    Detalhes do Empreendedor
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                        <CircularProgress size={isMobile ? 30 : 40} sx={{ color: '#10641c' }} />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        {error}
                    </Alert>
                ) : !user ? (
                    <Typography sx={{ textAlign: 'center', fontSize: isMobile ? '0.9rem' : '1rem', color: '#424242' }}>
                        Nenhum dado encontrado.
                    </Typography>
                ) : (
                    <>
                        <Paper
                            elevation={3}
                            sx={{
                                p: isMobile ? 3 : 4,
                                borderRadius: 3,
                                backgroundColor: '#f9f9f9',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                transition: 'box-shadow 0.3s ease-in-out',
                                '&:hover': { boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.1)' },
                                animation: 'fadeIn 0.5s ease-in-out',
                                '@keyframes fadeIn': {
                                    from: { opacity: 0, transform: 'translateY(10px)' },
                                    to: { opacity: 1, transform: 'translateY(0)' },
                                },
                                minHeight: isMobile ? '420px' : '420px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 2,
                                        mb: 2,
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                    }}
                                >
                                    {sections.map((section, index) => (
                                        <Tooltip key={index} title={section.title}>
                                            <Box
                                                onClick={() => handleSectionChange(index)}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    backgroundColor: currentSection === index ? '#10641c' : '#e0e0e0',
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1.5rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease-in-out',
                                                    '&:hover': {
                                                        backgroundColor: currentSection === index ? '#0a3d0f' : '#d0d0d0',
                                                        transform: 'scale(1.1)',
                                                    },
                                                }}
                                            >
                                                {React.cloneElement(section.icon, {
                                                    sx: { color: '#fff', fontSize: 'inherit' },
                                                })}
                                            </Box>
                                        </Tooltip>
                                    ))}
                                </Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        color: '#10641c',
                                        fontWeight: '500',
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {sections[currentSection].icon}
                                    {sections[currentSection].title}
                                </Typography>
                                <Divider sx={{ mb: 2, borderColor: '#e0e0e0' }} />
                                {renderSectionContent()}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    justifyContent:
                                        currentSection === 0
                                            ? 'flex-end'
                                            : currentSection === sections.length - 1
                                                ? 'flex-start'
                                                : 'space-between',
                                    mt: 'auto',
                                    pt: 2,
                                    borderTop: '1px solid #e0e0e0',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: '#f9f9f9',
                                    zIndex: 1,
                                }}
                            >
                                {currentSection !== 0 && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => handleSectionChange(currentSection - 1)}
                                        sx={{
                                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                                            borderColor: '#10641c',
                                            color: '#10641c',
                                            padding: isMobile ? '6px 12px' : '8px 16px',
                                            borderRadius: '8px',
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                borderColor: '#0a3d0f',
                                                color: '#0a3d0f',
                                                backgroundColor: '#e8f5e9',
                                                transform: 'scale(1.05)',
                                            },
                                            '&:disabled': {
                                                borderColor: '#e0e0e0',
                                                color: '#bdbdbd',
                                            },
                                        }}
                                    >
                                        Anterior
                                    </Button>
                                )}
                                {currentSection !== sections.length - 1 && (
                                    <Button
                                        variant="outlined"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => handleSectionChange(currentSection + 1)}
                                        sx={{
                                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                                            borderColor: '#10641c',
                                            color: '#10641c',
                                            padding: isMobile ? '6px 12px' : '8px 16px',
                                            borderRadius: '8px',
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                borderColor: '#0a3d0f',
                                                color: '#0a3d0f',
                                                backgroundColor: '#e8f5e9',
                                                transform: 'scale(1.05)',
                                            },
                                            '&:disabled': {
                                                borderColor: '#e0e0e0',
                                                color: '#bdbdbd',
                                            },
                                        }}
                                    >
                                        Próximo
                                    </Button>
                                )}
                            </Box>
                        </Paper>

                        <Paper
                            elevation={3}
                            sx={{
                                p: isMobile ? 2 : 4,
                                borderRadius: 3,
                                backgroundColor: '#ffffff',
                                border: '2px solid #10641c',
                                mt: 0,
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: '#10641c',
                                    fontWeight: '500',
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                }}
                            >
                                <Assignment sx={{ mr: 1, color: '#10641c' }} />
                                Formulários
                            </Typography>
                            <Divider sx={{ mb: 2, borderColor: '#e0e0e0' }} />
                            {forms && forms.length > 0 ? (
                                forms.map(fm => {
                                    const userHasResponded = fm.responses?.some(res => res.answeredBy === user.id) || false;

                                    return (
                                        <Box
                                            key={fm.id}
                                            sx={{
                                                mb: 2,
                                                display: 'flex',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                alignItems: isMobile ? 'flex-start' : 'center',
                                                justifyContent: 'space-between',
                                                gap: isMobile ? 1 : 2,
                                            }}
                                        >
                                            <Tooltip title={fm.description}>
                                                <Typography
                                                    sx={{
                                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                                        color: '#424242',
                                                    }}
                                                >
                                                    {fm.name}
                                                </Typography>
                                            </Tooltip>

                                            {userHasResponded ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<AssignmentTurnedIn />}
                                                        sx={{
                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                            backgroundColor: '#757575',
                                                            color: '#FFF',
                                                            padding: isMobile ? '4px 8px' : '8px 16px',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.3s ease-in-out',
                                                            width: isMobile ? '70%' : 'auto',
                                                            borderColor: '#616161',
                                                            '&:hover': {
                                                                backgroundColor: '#616161',
                                                                color: '#FFF',
                                                                borderColor: '#424242',
                                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                                            },
                                                        }}
                                                    >
                                                        Já respondido
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => navigate(`/${typeUser}/forms/edit/${fm.id}/${user.id}`)}
                                                        sx={{
                                                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                            borderColor: '#10641c',
                                                            color: '#10641c',
                                                            padding: isMobile ? '3px 12px' : '8px 16px',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.3s ease-in-out',
                                                            '&:hover': {
                                                                borderColor: '#0a3d0f',
                                                                color: '#0a3d0f',
                                                                backgroundColor: '#e8f5e9',
                                                                transform: 'scale(1.05)',
                                                            },
                                                            '&:disabled': {
                                                                borderColor: '#e0e0e0',
                                                                color: '#bdbdbd',
                                                            },
                                                        }}
                                                    >
                                                        Editar
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<AssignmentAdd />}
                                                    onClick={() => navigate(`/${typeUser}/forms/response/${fm.id}/${user.id}`)}
                                                    sx={{
                                                        fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                        backgroundColor: '#10641c',
                                                        color: '#FFF',
                                                        padding: isMobile ? '4px 8px' : '8px 16px',
                                                        borderRadius: '8px',
                                                        transition: 'all 0.3s ease-in-out',
                                                        width: isMobile ? '40%' : 'auto',
                                                        '&:hover': {
                                                            backgroundColor: '#0a3d0f',
                                                            color: '#FFF',
                                                            border: '1px solid #0a3d0f',
                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                                        },
                                                    }}
                                                >
                                                    Responder
                                                </Button>
                                            )}
                                        </Box>
                                    );
                                })
                            ) : (
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        color: '#424242',
                                    }}
                                >
                                    Nenhum formulário adicional necessário!
                                </Typography>
                            )}
                        </Paper>
                    </>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignSelf: 'flex-start', mt: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/${typeUser}/entrepreneur`)}
                        sx={{
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                            borderColor: '#10641c',
                            color: '#10641c',
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                borderColor: '#0a3d0f',
                                color: '#0a3d0f',
                                backgroundColor: '#e8f5e9',
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/${typeUser}/entrepreneur/edit/${id}`)}
                        sx={{
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                            backgroundColor: '#10641c',
                            color: '#FFF',
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                backgroundColor: '#0a3d0f',
                                color: '#FFF',
                                border: '1px solid #0a3d0f',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                            },
                        }}
                    >
                        Editar
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default DetailsEntrepreneur;